import React, { useState } from 'react';
import {
  Box,
  Flex,
  Button,
  Divider,
  Text,
  useToast,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaTwitter, FaMarkdown, FaClock } from 'react-icons/fa';

interface ControlPanelProps {
  markdownContent?: string;
}

const ControlPanel: React.FC<ControlPanelProps> = ({ markdownContent = '' }) => {
  const toast = useToast();
  const { isOpen: isTwitterOpen, onOpen: onTwitterOpen, onClose: onTwitterClose } = useDisclosure();
  const { isOpen: isSaveOpen, onOpen: _onSaveOpen, onClose: onSaveClose } = useDisclosure();
  
  const [twitterText, setTwitterText] = useState('');
  const [fileName, setFileName] = useState('document.md');

  // カラーモード対応
  const controlPanelBg = useColorModeValue('gray.50', 'gray.700');

  // Markdownをクリップボードにコピー
  const handleCopyMarkdown = () => {
    if (!markdownContent) {
      toast({
        title: 'コピーするコンテンツがありません',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // クリップボードにコピー
    navigator.clipboard.writeText(markdownContent)
      .then(() => {
        toast({
          title: 'Markdownをクリップボードにコピーしました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      })
      .catch(err => {
        toast({
          title: 'コピーに失敗しました',
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  // Xへの投稿処理
  const handleTwitterPost = () => {
    // プロトタイプでは実際の連携は行わず、成功メッセージのみ表示
    toast({
      title: 'Xへの投稿準備が完了しました',
      description: 'プロトタイプでは実際の投稿は行われません',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onTwitterClose();
  };


  // ファイル保存処理
  const handleFileSave = () => {
    if (!markdownContent) {
      toast({
        title: '保存するコンテンツがありません',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // プロトタイプでは実際のファイル保存は行わず、成功メッセージのみ表示
    toast({
      title: 'ファイル保存の準備が完了しました',
      description: 'プロトタイプでは実際の保存は行われません',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onSaveClose();
  };

  return (
    <>
      <Box borderWidth="1px" borderRadius="lg" p={4} bg={controlPanelBg}>
        <Text fontWeight="bold" mb={2}>書き出し</Text>
        <Divider mb={3} />
        
        <Flex wrap="wrap" gap={2}>
          <Button 
            leftIcon={<Icon as={FaTwitter} />} 
            colorScheme="twitter" 
            size="sm"
            onClick={onTwitterOpen}
            isDisabled={!markdownContent}
          >
            Xへ投稿
          </Button>
          
          
          <Button 
            leftIcon={<Icon as={FaMarkdown} />} 
            colorScheme="purple" 
            size="sm"
            onClick={handleCopyMarkdown}
            isDisabled={!markdownContent}
          >
            Markdownコピー
          </Button>
          
          <Button 
            leftIcon={<Icon as={FaClock} />} 
            colorScheme="gray" 
            size="sm"
            isDisabled={true}
            opacity={0.6}
            cursor="not-allowed"
            title="ファイル保存機能は準備中です"
          >
            ファイル保存 (準備中)
          </Button>
        </Flex>
      </Box>

      {/* X投稿モーダル */}
      <Modal isOpen={isTwitterOpen} onClose={onTwitterClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Xへ投稿</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>投稿内容</FormLabel>
              <Textarea 
                value={twitterText || markdownContent.substring(0, 280)} 
                onChange={(e) => setTwitterText(e.target.value)}
                maxLength={280}
                placeholder="投稿内容を入力してください"
                rows={6}
              />
              <Text fontSize="xs" mt={1} textAlign="right">
                {twitterText.length}/280文字
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTwitterClose}>
              キャンセル
            </Button>
            <Button colorScheme="twitter" onClick={handleTwitterPost}>
              投稿
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      {/* ファイル保存モーダル */}
      <Modal isOpen={isSaveOpen} onClose={onSaveClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ファイル保存</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>ファイル名</FormLabel>
              <Input 
                value={fileName} 
                onChange={(e) => setFileName(e.target.value)}
                placeholder="ファイル名を入力"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSaveClose}>
              キャンセル
            </Button>
            <Button colorScheme="teal" onClick={handleFileSave}>
              保存
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ControlPanel;
